import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { detectLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE } from "@/lib/i18n";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Clusters from "@/pages/Clusters";
import ClusterDetail from "@/pages/ClusterDetail";
import Capabilities from "@/pages/Capabilities";
import Work from "@/pages/Work";
import CaseStudy from "@/pages/CaseStudy";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import CategoryDetail from "@/pages/CategoryDetail";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Trends from "@/pages/Trends";
import TrendsPost from "@/pages/TrendsPost";
import Regions from "@/pages/Regions";
import RegionDetail from "@/pages/RegionDetail";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudySlugRouter from "@/pages/CaseStudySlugRouter";
import StartAProject from "@/pages/StartAProject";
import Industries from "@/pages/Industries";
import Usa from "@/pages/Usa";
import UsaCommercial from "@/pages/UsaCommercial";
import UsaState from "@/pages/UsaState";
import RegionCity from "@/pages/RegionCity";
import AuthorityService from "@/pages/AuthorityService";
import HomeDecorCategory from "@/pages/HomeDecorCategory";
import CategoryIndex from "@/pages/CategoryIndex";
import CategoryHub from "@/pages/CategoryHub";
import { HOME_DECOR_SLUGS } from "@/data/homeDecor";
import { HOME_DECOR_WAVE1_LOCALES } from "@/data/homeDecorLocales";
import TradeFairs from "@/pages/TradeFairs";
import TradeFairDetail from "@/pages/TradeFairDetail";
import RegionCategory from "@/pages/RegionCategory";
import NordicHub from "@/pages/NordicHub";
import LocalizedRegion from "@/pages/LocalizedRegion";
import ProgrammesIndex from "@/pages/ProgrammesIndex";
import ProgrammeDetail from "@/pages/ProgrammeDetail";
import NotFound from "@/pages/NotFound";
import Ask from "@/pages/Ask";
import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";
import AdminSeo from "@/pages/AdminSeo";
import AdminAiImageStudio from "@/pages/AdminAiImageStudio";
import { AuthProvider, AuthCallback, ProtectedRoute } from "@/lib/auth";
import { StartProjectProvider } from "@/lib/startProjectContext";
import { useParams } from "react-router-dom";

// Redirects /news/:id → /trends/:id (permanent alias, canonical is /trends).
function NewsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/trends/${id}`} replace />;
}

// Redirects /regions/usa → /usa (canonical US hub).
function UsaLegacyRedirect() {
  return <Navigate to="/usa" replace />;
}

// Redirects legacy PHP-era duplicate homepage URLs (/index.php, /INDEX.PHP,
// /index.php/, etc.) to the canonical "/". The edge-level 301 in
// /public/_redirects handles this first at HTTP-status level; this SPA
// fallback catches any preview/dev environment that bypasses the edge.
function IndexPhpRedirect() {
  return <Navigate to="/" replace />;
}

// Public English routes — shared between the main router and the locale
// fallback gate (/<locale>/* renders these with the English master content
// until a real translation exists for that locale+page).
const PUBLIC_ROUTES = (
  <>
    <Route path="/" element={<Home />} />
    {/* Historic PHP-era duplicate homepage URLs. 301 handled at the edge
       via /public/_redirects; SPA fallback below covers preview/dev. */}
    <Route path="/index.php" element={<IndexPhpRedirect />} />
    <Route path="/INDEX.PHP" element={<IndexPhpRedirect />} />
    <Route path="/Index.php" element={<IndexPhpRedirect />} />
    <Route path="/index.PHP" element={<IndexPhpRedirect />} />
    <Route path="/clusters" element={<Clusters />} />
    <Route path="/clusters/:id" element={<ClusterDetail />} />
    <Route path="/capabilities" element={<Capabilities />} />
    <Route path="/work" element={<Work />} />
    <Route path="/work/:slug" element={<CaseStudy />} />
    <Route path="/categories" element={<CategoryIndex />} />
    {/* Sprint 30 · 5 top-level category hubs — MUST precede /categories/:id */}
    <Route path="/categories/tabletop-dining" element={<CategoryHub />} />
    <Route path="/categories/candles-home-fragrance" element={<CategoryHub />} />
    <Route path="/categories/garden-outdoor" element={<CategoryHub />} />
    <Route path="/categories/bathroom-wellness" element={<CategoryHub />} />
    <Route path="/categories/seasonal-decor" element={<CategoryHub />} />
    <Route path="/categories/:id" element={<CategoryDetail />} />
    <Route path="/services" element={<Services />} />
    <Route path="/services/:id" element={<ServiceDetail />} />
    <Route path="/products" element={<Products />} />
    <Route path="/products/:id" element={<ProductDetail />} />
    <Route path="/trends" element={<Trends />} />
    <Route path="/trends/:id" element={<TrendsPost />} />
    <Route path="/regions" element={<Regions />} />
    {/* /regions/usa → /usa 301 (canonical US hub). SPA-level fallback
       behind the edge-level 301 in /public/_redirects. */}
    <Route path="/regions/usa" element={<UsaLegacyRedirect />} />
    {/* International city-level India sourcing pages (Phase 1: 8 cities) */}
    <Route path="/regions/uk/london" element={<RegionCity />} />
    <Route path="/regions/uk/manchester" element={<RegionCity />} />
    <Route path="/regions/germany/berlin" element={<RegionCity />} />
    <Route path="/regions/germany/munich" element={<RegionCity />} />
    <Route path="/regions/france/paris" element={<RegionCity />} />
    <Route path="/regions/france/lyon" element={<RegionCity />} />
    <Route path="/regions/netherlands/amsterdam" element={<RegionCity />} />
    <Route path="/regions/netherlands/rotterdam" element={<RegionCity />} />
    {/* Phase 1 · UK category programmes (7 pages) — MUST precede /regions/:id */}
    <Route path="/regions/uk/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/home-decor-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/kitchenware-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/home-textile-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/private-label-sourcing" element={<RegionCategory />} />
    <Route path="/regions/uk/india-buying-agent" element={<RegionCategory />} />
    {/* Phase 3 · Netherlands category programmes (4 pages) */}
    <Route path="/regions/netherlands/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/netherlands/home-decor-sourcing" element={<RegionCategory />} />
    <Route path="/regions/netherlands/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/netherlands/home-textile-sourcing" element={<RegionCategory />} />
    {/* Sprint A · Global Furniture + Lighting SEO — Tier-1 EU country×category pages */}
    <Route path="/regions/germany/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/germany/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/france/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/france/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/sweden/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/sweden/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/finland/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/finland/lighting-sourcing" element={<RegionCategory />} />
    {/* Phase 5 · Nordic sustainability hub */}
    <Route path="/regions/nordic-sustainable-sourcing" element={<NordicHub />} />
    {/* Phase 6 · Illustrative Sourcing Programmes */}
    <Route path="/programmes" element={<ProgrammesIndex />} />
    <Route path="/programmes/:slug" element={<ProgrammeDetail />} />
    <Route path="/regions/:id" element={<RegionDetail />} />
    {/* Canonical US hub + six US commercial programme pages */}
    <Route path="/usa" element={<Usa />} />
    <Route path="/india-sourcing-agent-for-usa" element={<UsaCommercial />} />
    <Route path="/india-buying-agent-for-usa" element={<UsaCommercial />} />
    <Route path="/india-quality-control-for-usa" element={<UsaCommercial />} />
    <Route path="/india-factory-inspection-for-usa" element={<UsaCommercial />} />
    <Route path="/india-supplier-verification-for-usa" element={<UsaCommercial />} />
    <Route path="/india-product-development-for-usa" element={<UsaCommercial />} />
    {/* US state-level India sourcing pages (8 states) */}
    <Route path="/india-sourcing-new-york" element={<UsaState />} />
    <Route path="/india-sourcing-california" element={<UsaState />} />
    <Route path="/india-sourcing-texas" element={<UsaState />} />
    <Route path="/india-sourcing-florida" element={<UsaState />} />
    <Route path="/india-sourcing-illinois" element={<UsaState />} />
    <Route path="/india-sourcing-georgia" element={<UsaState />} />
    <Route path="/india-sourcing-ohio" element={<UsaState />} />
    <Route path="/india-sourcing-washington" element={<UsaState />} />
    {/* AI-search FAQ knowledge base */}
    <Route path="/ask" element={<Ask />} />
    {/* /news is a permanent alias of /trends — 301 to canonical /trends URLs */}
    <Route path="/news" element={<Navigate to="/trends" replace />} />
    <Route path="/news/:id" element={<NewsRedirect />} />
    <Route path="/case-studies" element={<CaseStudies />} />
    <Route path="/case-studies/:slug" element={<CaseStudySlugRouter />} />
    <Route path="/start-a-project" element={<StartAProject />} />
    <Route path="/industries" element={<Industries />} />
    <Route path="/about" element={<About />} />
    <Route path="/studio" element={<Navigate to="/about" replace />} />
    <Route path="/contact" element={<Contact />} />
    {/* Sprint E · Trade Fair SEO authority hub */}
    <Route path="/trade-fairs" element={<TradeFairs />} />
    <Route path="/trade-fairs/:slug" element={<TradeFairDetail />} />
    {/* Sprint F · Authority-service SEO cluster (6 pages) — additive, existing SEO untouched */}
    <Route path="/services/furniture-sourcing-company-india" element={<AuthorityService />} />
    <Route path="/services/furniture-quality-control-india" element={<AuthorityService />} />
    <Route path="/services/table-lamp-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/wall-light-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/floor-lamp-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/chandelier-sourcing-india" element={<AuthorityService />} />
    {/* Sprint 15 · SEO expansion (12 additive · zero touch to existing SEO) */}
    <Route path="/services/furniture-sourcing-agent-india" element={<AuthorityService />} />
    <Route path="/services/furniture-manufacturers-india" element={<AuthorityService />} />
    <Route path="/services/furniture-suppliers-india" element={<AuthorityService />} />
    <Route path="/services/furniture-procurement-india" element={<AuthorityService />} />
    <Route path="/services/wooden-furniture-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/solid-wood-furniture-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/lighting-sourcing-company-india" element={<AuthorityService />} />
    <Route path="/services/lighting-sourcing-agent-india" element={<AuthorityService />} />
    <Route path="/services/lighting-manufacturers-india" element={<AuthorityService />} />
    <Route path="/services/lighting-suppliers-india" element={<AuthorityService />} />
    <Route path="/services/pendant-light-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/lighting-quality-control-india" element={<AuthorityService />} />
    {/* Sprint 24 · SEO + Positioning expansion (8 additive · zero touch) */}
    <Route path="/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/services/product-development-india" element={<AuthorityService />} />
    <Route path="/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    {/* Sprint C · Country × Category Tier-2 expansion (10 additive pages) */}
    <Route path="/regions/canada/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/canada/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/spain/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/spain/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/italy/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/italy/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/regions/poland/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/regions/poland/lighting-sourcing" element={<RegionCategory />} />
    <Route path="/usa/furniture-sourcing" element={<RegionCategory />} />
    <Route path="/usa/lighting-sourcing" element={<RegionCategory />} />
    {/* Sprint 28 · Home Décor category expansion — 26 new category pages */}
    {HOME_DECOR_SLUGS.map((slug) => (
      <Route key={`hd-${slug}`} path={`/categories/home-decor/${slug}`} element={<HomeDecorCategory />} />
    ))}
    {/* Sprint 29 · Home Décor Wave 1 translations — DE / FR / ES × 26 routes */}
    {HOME_DECOR_WAVE1_LOCALES.map((lc) =>
      HOME_DECOR_SLUGS.map((slug) => (
        <Route key={`${lc}-hd-${slug}`} path={`/${lc}/categories/home-decor/${slug}`} element={<HomeDecorCategory />} />
      ))
    )}
  </>
);

// Fully-translated localized routes (de/fr/es) — explicit, static, and
// outrank the /:locale/* fallback gate below.
const LOCALIZED_ROUTES = (
  <>
    {/* Phase 2 · German-localized (/de) — hub + 3 cities */}
    <Route path="/de/beschaffung-indien" element={<LocalizedRegion />} />
    <Route path="/de/beschaffung-indien/hamburg" element={<LocalizedRegion />} />
    <Route path="/de/beschaffung-indien/frankfurt" element={<LocalizedRegion />} />
    <Route path="/de/beschaffung-indien/koeln" element={<LocalizedRegion />} />
    {/* Phase 4 · French-localized (/fr) — hub + 2 cities */}
    <Route path="/fr/approvisionnement-inde" element={<LocalizedRegion />} />
    <Route path="/fr/approvisionnement-inde/marseille" element={<LocalizedRegion />} />
    <Route path="/fr/approvisionnement-inde/lille" element={<LocalizedRegion />} />
    {/* Sprint 24 · Multilingual Phase 1 — DE / FR / ES · hreflang-paired */}
    <Route path="/de/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/de/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/de/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/de/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/de/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/de/services/product-development-india" element={<AuthorityService />} />
    <Route path="/de/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/de/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/fr/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/fr/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/fr/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/fr/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/fr/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/fr/services/product-development-india" element={<AuthorityService />} />
    <Route path="/fr/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/fr/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/es/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/es/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/es/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/es/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/es/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/es/services/product-development-india" element={<AuthorityService />} />
    <Route path="/es/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/es/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    {/* Sprint 27 · Multilingual Phase 2 — IT / NL / SV / DA / FI / AR (6 × 8 = 48 routes) */}
    <Route path="/it/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/it/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/it/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/it/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/it/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/it/services/product-development-india" element={<AuthorityService />} />
    <Route path="/it/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/it/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/nl/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/nl/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/nl/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/nl/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/nl/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/nl/services/product-development-india" element={<AuthorityService />} />
    <Route path="/nl/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/nl/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/sv/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/sv/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/sv/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/sv/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/sv/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/sv/services/product-development-india" element={<AuthorityService />} />
    <Route path="/sv/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/sv/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/da/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/da/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/da/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/da/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/da/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/da/services/product-development-india" element={<AuthorityService />} />
    <Route path="/da/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/da/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/fi/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/fi/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/fi/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/fi/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/fi/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/fi/services/product-development-india" element={<AuthorityService />} />
    <Route path="/fi/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/fi/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
    <Route path="/ar/services/buying-house-india" element={<AuthorityService />} />
    <Route path="/ar/services/strategic-sourcing-india" element={<AuthorityService />} />
    <Route path="/ar/services/supply-chain-management-india" element={<AuthorityService />} />
    <Route path="/ar/services/factory-sourcing-india" element={<AuthorityService />} />
    <Route path="/ar/services/quality-control-india" element={<AuthorityService />} />
    <Route path="/ar/services/product-development-india" element={<AuthorityService />} />
    <Route path="/ar/services/export-documentation-india" element={<AuthorityService />} />
    <Route path="/ar/industries/home-lifestyle-sourcing-india" element={<AuthorityService />} />
  </>
);

/**
 * Locale fallback gate (Sprint 26). Any /<locale>/… path without an explicit
 * translated route renders the matching English master page — never a 404.
 * The descendant <Routes> below automatically matches against the remaining
 * pathname after the parent /:locale/* match (no location prop — passing one
 * would strip the locale base twice). Canonical/og:url/json-ld still point
 * at the English URL (each page's useSeo path is the English slug), and
 * <html lang> is forced back to English because the rendered content is the
 * English master copy. Genuinely invalid paths (unknown locale or unknown
 * page) keep the custom 404 experience.
 */
function LocaleGate() {
  const location = useLocation();
  const seg = (location.pathname || "/").match(/^\/([a-z]{2})(?:\/|$)/);
  const code = seg ? seg[1] : null;
  const locDef = SUPPORTED_LOCALES.find((l) => l.code === code && l.pathPrefix);
  const valid = Boolean(locDef) && code !== DEFAULT_LOCALE;

  // Render-phase override: AppRouter syncs <html lang/dir> from the URL
  // locale during render; for fallback pages the content is the English
  // master copy, so the child gate (rendered after the parent) wins.
  if (valid && typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", "en");
    document.documentElement.setAttribute("dir", "ltr");
  }

  if (!valid) return <NotFound />;
  return (
    <Routes>
      {PUBLIC_ROUTES}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect Emergent Auth callback during render (NOT in useEffect).
  // Prevents race conditions where ProtectedRoute redirects to /login before
  // the session_id from the URL fragment is exchanged for a session_token.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Sync <html lang> + dir with current URL locale for accessibility + hreflang.
  const loc = detectLocale(location.pathname);
  const dir = SUPPORTED_LOCALES.find((l) => l.code === loc)?.dir || "ltr";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", loc);
    document.documentElement.setAttribute("dir", dir);
  }
  return (
    <Routes>
      {/* Admin (no main layout) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute adminOnly>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/briefs"
        element={
          <ProtectedRoute adminOnly>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/seo"
        element={
          <ProtectedRoute adminOnly>
            <AdminSeo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-image-studio"
        element={
          <ProtectedRoute adminOnly>
            <AdminAiImageStudio />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<Navigate to="/admin/leads" replace />} />

      {/* Public site (with header/footer) */}
      <Route element={<Layout />}>
        {PUBLIC_ROUTES}
        {LOCALIZED_ROUTES}
        {/* Locale fallback gate — /<locale>/… renders the English master
           page (canonical → English) until a translation exists. */}
        <Route path="/:locale/*" element={<LocaleGate />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StartProjectProvider>
          <AppRouter />
        </StartProjectProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
